import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useImpersonationUsers, useCanImpersonate } from '@/queries/useImpersonation';
import { useAuthStore } from '@/stores/useAuthStore';
import permissionsApi from '@/api/permissionsApi';
import type { ImpersonationUser } from '@/auth/permissions.types';
import { useHomeNavigation } from '@/hooks/useHomeNavigation';
import { Section, SectionHeader, SectionCard } from '@/components/common';

export function ImpersonatePage() {
  const queryClient = useQueryClient();
  const setImpersonatedUser = useAuthStore((state) => state.setImpersonatedUser);
  const setPermissions = useAuthStore((state) => state.setPermissions);
  const { navigateToHome } = useHomeNavigation();

  const { data: canImpersonate, isLoading: isLoadingPermission } = useCanImpersonate();
  const { data: users, isLoading: isLoadingUsers } = useImpersonationUsers(canImpersonate === true);

  const [selectedUser, setSelectedUser] = useState<ImpersonationUser | null>(null);

  const handleImpersonate = async () => {
    if (selectedUser) {
      // Set impersonated user first (this affects the API header)
      setImpersonatedUser(selectedUser);

      // Fetch new permissions directly and update store
      const newPermissions = await permissionsApi.getMyPermissions();
      setPermissions(newPermissions);

      // Navigate BEFORE clearing cache to avoid 403 errors on current page refetch
      navigateToHome(newPermissions?.globalRoles);
      queryClient.clear();
    }
  };

  if (isLoadingPermission) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canImpersonate) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" color="error">
          You do not have permission to impersonate users.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Section>
        <SectionHeader>IMPERSONATE USER</SectionHeader>
        <SectionCard>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Select a user to impersonate. You will be able to view and perform actions as that user.
          </Typography>

          <Autocomplete
            options={users || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={selectedUser}
            onChange={(_, newValue) => setSelectedUser(newValue)}
            loading={isLoadingUsers}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select User"
                placeholder="Start typing to search..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 3, maxWidth: 600 }}
          />

          <Button
            variant="contained"
            color="warning"
            onClick={handleImpersonate}
            disabled={!selectedUser}
          >
            Start Impersonation
          </Button>
        </SectionCard>
      </Section>
    </Box>
  );
}

export default ImpersonatePage;
